import _ from 'underscore';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import {
  getCashbackInheritOptions,
  getParentLevelCashbackPercent,
  isCashbackPercentValid,
  getCashbackPercentValidationError,
  getRealCashbackPercentValue,
} from '@bingads-webui-campaign/personalized-offer-shared';

// Valid Bid Adjustment limits: Decrease by 90% to Increase by 900%
const bidAjdLimits = {
  getMaxMinByType: (criterionType) => {
    const max = 900;
    let min = -90;

    if (criterionType === 'Device') {
      min = -100;
    }

    return {
      max,
      min,
    };
  },
};

const getBidAdjustmentOptions = () => {
  const i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');

  return {
    percentage: 0,
    availableOptions: [
      { key: 'inc', text: i18n.getString('Audience_Campaign_Settings_BidAdjustment_Increase') },
      { key: 'dec', text: i18n.getString('Audience_Campaign_Settings_BidAdjustment_Decrease') },
    ],
    selectedOption: 'inc',
  };
};

/**
  * Function to compute bid adjustment
  * @param {string} rawBidPercentage Bid percentage input
  * @param {string} bidAdjOptionText Bid adjustment option input
  *
  * @returns {int} Computed Bid adjustment value
  */
const computeBidAdjustment = (rawBidPercentage, bidAdjOptionText) => {
  const percentage = Number(rawBidPercentage);

  const bidAdjustmentOptions = getBidAdjustmentOptions();
  const bidAdjOption = _.find(
    bidAdjustmentOptions.availableOptions,
    option => option.text === bidAdjOptionText
  );

  const bidAdjOptionKey = _.result(bidAdjOption, 'key');

  return bidAdjOptionKey === 'dec' ? -percentage : percentage;
};

/**
  * Function to validate bid adjustment
  * @param {string} val Bid percentage input
  * @param {string} criterionType - type of the criterion
  * @param {boolean} isAudienceCampaign - is audience campaign or not
  *
  * @returns {bool} is valid
  */
const isBidAdjustmentValid = (val, criterionType) => {
  const { max, min } = bidAjdLimits.getMaxMinByType(criterionType);

  return (val < 0 && val >= min) ||
    (val >= 0 && val <= max);
};

/**
  * Function to get bid adjustment validation error
  * @param {string} v Bid percentage input
  * @param {string} criterionType - type of the criterion
  * @param {boolean} isEnableCustomMessage - is custom message enabled or not
  *
  * @returns {string} validation error
  */
const getValidationError = (v, criterionType, isEnableCustomMessage) => {
  const val = Number(v);
  const i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
  const { max, min } = bidAjdLimits.getMaxMinByType(criterionType);
  let validationError = '';

  if (_.isNaN(val)) {
    validationError = i18n.getString('Audience_Campaign_Settings_Bid_Invalid');
  } else if (val < 0) {
    if (val === -100 && isEnableCustomMessage && criterionType === 'Age') {
      validationError = i18n.getString('Audience_Campaign_Settings_Bid_Validation_Decrease_100_Setting', { min: 0, max: Math.abs(min) });
    } else {
      validationError = i18n.getString('Audience_Campaign_Settings_Bid_Validation', { min: 0, max: Math.abs(min) });
    }
  } else {
    validationError = i18n.getString('Audience_Campaign_Settings_Bid_Validation', { min: 0, max });
  }

  return validationError;
};

export {
  getBidAdjustmentOptions,
  computeBidAdjustment,
  isBidAdjustmentValid,
  isCashbackPercentValid,
  getValidationError,
  getCashbackPercentValidationError,
  getRealCashbackPercentValue,
  getCashbackInheritOptions,
  getParentLevelCashbackPercent,
};
